import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { reportsAPI } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { BarChart3, Download, Package, TrendingUp, Wrench } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import ReportsModulePage from '@/pages/ReportsModulePage';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { safeRequest } from '@/lib/safeRequest';

const EMPTY_ARRAY = [];

export default function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [salesBySeller, setSalesBySeller] = useState(EMPTY_ARRAY);
  const [inventoryValue, setInventoryValue] = useState(0);
  const [topProducts, setTopProducts] = useState(EMPTY_ARRAY);
  const [servicePerformance, setServicePerformance] = useState(EMPTY_ARRAY);
  const activeTab = searchParams.get('tab') || 'overview';
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();

  const loadReports = useCallback(async () => {
    try {
      resetFetchTelemetry();
      const [salesRes, inventoryRes, productsRes, servicesRes] = await Promise.all([
        safeRequest(() => reportsAPI.getSalesBySeller(), { data: EMPTY_ARRAY }, { telemetry: { module: 'reports', endpoint: 'getSalesBySeller', onEvent: captureFetchTelemetry } }),
        safeRequest(() => reportsAPI.getInventoryValue(), { data: { total_inventory_value: 0 } }, { telemetry: { module: 'reports', endpoint: 'getInventoryValue', onEvent: captureFetchTelemetry } }),
        safeRequest(() => reportsAPI.getTopProducts(5), { data: EMPTY_ARRAY }, { telemetry: { module: 'reports', endpoint: 'getTopProducts', onEvent: captureFetchTelemetry } }),
        safeRequest(() => reportsAPI.getServicePerformance(), { data: EMPTY_ARRAY }, { telemetry: { module: 'reports', endpoint: 'getServicePerformance', onEvent: captureFetchTelemetry } }),
      ]);
      setSalesBySeller(salesRes.data || EMPTY_ARRAY);
      setInventoryValue(inventoryRes.data?.total_inventory_value || 0);
      setTopProducts(productsRes.data || EMPTY_ARRAY);
      setServicePerformance(servicesRes.data || EMPTY_ARRAY);
    } catch {
      toast.error('Erro ao carregar relatórios consolidados');
    } finally {
      setLoading(false);
    }
  }, [captureFetchTelemetry, resetFetchTelemetry]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const topSeller = useMemo(() => salesBySeller[0] || null, [salesBySeller]);
  const topTechnician = useMemo(() => servicePerformance[0] || null, [servicePerformance]);
  const hasReportsData = Boolean(salesBySeller.length || inventoryValue || topProducts.length || servicePerformance.length);

  const exportToCSV = useCallback((rows, filename) => {
    if (!Array.isArray(rows) || rows.length === 0) {
      toast.info('Sem dados para exportar');
      return;
    }

    const csv = [
      Object.keys(rows[0]).join(','),
      ...rows.map((row) => Object.values(row).join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    window.URL.revokeObjectURL(url);
  }, []);

  return (
    <ModuleStateBoundary
      loading={loading}
      hasData={hasReportsData}
      partialIssues={partialIssues}
      loadingTestId="reports-page-loading"
      partialIssuesTestId="reports-page-partial-issues"
      emptyState={
        <div className="space-y-6 p-6" data-testid="reports-page">
          <EmptyStatePanel
            title="Sem dados disponíveis"
            description="Nenhum registo encontrado para o centro de relatórios. Pode configurar ou importar dados para começar."
            testId="reports-page-empty-state"
          />
        </div>
      }
    >
    <div className="space-y-6 p-6" data-testid="reports-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight">Centro Unificado de Relatórios</h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Analytics executiva, geração operacional, exportação, arquivo e agendamento num único módulo.
          </p>
        </div>
        <Button variant="outline" onClick={() => setSearchParams({ tab: 'operational' })} data-testid="open-operational-reports-button">
          <BarChart3 className="mr-2 h-4 w-4" />
          Abrir gerador operacional
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card data-testid="reports-inventory-value-card">
          <CardHeader className="pb-2">
            <CardDescription>Valor actual do inventário</CardDescription>
            <CardTitle className="text-3xl">{formatCurrency(inventoryValue)}</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-2 text-sm text-muted-foreground">
            <Package className="h-4 w-4" /> Baseado no custo registado do stock.
          </CardContent>
        </Card>

        <Card data-testid="reports-top-seller-card">
          <CardHeader className="pb-2">
            <CardDescription>Melhor vendedor</CardDescription>
            <CardTitle className="text-2xl">{topSeller?.seller_name || 'Sem dados'}</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-2 text-sm text-muted-foreground">
            <TrendingUp className="h-4 w-4" /> {topSeller ? `${topSeller.total_sales} venda(s) | ${formatCurrency(topSeller.total_revenue)}` : 'Nenhuma venda após o reset'}
          </CardContent>
        </Card>

        <Card data-testid="reports-top-technician-card">
          <CardHeader className="pb-2">
            <CardDescription>Melhor técnico</CardDescription>
            <CardTitle className="text-2xl">{topTechnician?.technician_name || 'Sem dados'}</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-2 text-sm text-muted-foreground">
            <Wrench className="h-4 w-4" /> {topTechnician ? `${topTechnician.completed_services}/${topTechnician.total_services} serviços concluídos` : 'Nenhum serviço após o reset'}
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setSearchParams({ tab: value })} className="space-y-4">
        <TabsList data-testid="reports-tabs-list">
          <TabsTrigger value="overview" data-testid="reports-tab-overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="operational" data-testid="reports-tab-operational">Operacional, Arquivo & Agendamento</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 xl:grid-cols-3">
            <Card data-testid="reports-sales-overview-card">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle>Vendas por vendedor</CardTitle>
                    <CardDescription>Resumo rápido do desempenho comercial.</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => exportToCSV(salesBySeller, 'sales-by-seller.csv')} data-testid="export-sales-overview-button">
                    <Download className="mr-2 h-4 w-4" /> Exportar
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {salesBySeller.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem dados de vendas disponíveis após o reset.</p>
                ) : salesBySeller.map((seller, index) => (
                  <div key={seller._id || seller.seller_name} className="flex items-center justify-between rounded-2xl border border-slate-200 p-3" data-testid={`seller-metric-${index}`}>
                    <div>
                      <p className="font-medium">{seller.seller_name}</p>
                      <p className="text-xs text-muted-foreground">{seller.total_sales} venda(s)</p>
                    </div>
                    <div className="text-right font-semibold text-indigo-600">{formatCurrency(seller.total_revenue)}</div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card data-testid="reports-products-overview-card">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle>Top produtos</CardTitle>
                    <CardDescription>Artigos com maior saída.</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => exportToCSV(topProducts, 'top-products.csv')} data-testid="export-products-overview-button">
                    <Download className="mr-2 h-4 w-4" /> Exportar
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {topProducts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem produtos vendidos após o reset.</p>
                ) : topProducts.map((product, index) => (
                  <div key={product._id || product.product_name} className="flex items-center justify-between rounded-2xl border border-slate-200 p-3" data-testid={`top-product-${index}`}>
                    <div>
                      <p className="font-medium">{product.product_name}</p>
                      <p className="text-xs text-muted-foreground">{product.total_quantity} unidade(s)</p>
                    </div>
                    <div className="text-right font-semibold text-emerald-600">{formatCurrency(product.total_revenue)}</div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card data-testid="reports-services-overview-card">
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle>Desempenho técnico</CardTitle>
                    <CardDescription>Execução dos serviços por técnico.</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => exportToCSV(servicePerformance, 'service-performance.csv')} data-testid="export-services-overview-button">
                    <Download className="mr-2 h-4 w-4" /> Exportar
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {servicePerformance.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sem serviços concluídos após o reset.</p>
                ) : servicePerformance.map((tech, index) => (
                  <div key={tech._id || tech.technician_name} className="flex items-center justify-between rounded-2xl border border-slate-200 p-3" data-testid={`service-performance-${index}`}>
                    <div>
                      <p className="font-medium">{tech.technician_name || 'Sem técnico'}</p>
                      <p className="text-xs text-muted-foreground">{tech.completed_services}/{tech.total_services} concluídos</p>
                    </div>
                    <div className="text-right font-semibold">{tech.total_services > 0 ? `${((tech.completed_services / tech.total_services) * 100).toFixed(1)}%` : '0%'}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="operational" className="space-y-4" data-testid="reports-operational-tab-content">
          <ReportsModulePage embedded />
        </TabsContent>
      </Tabs>
    </div>
    </ModuleStateBoundary>
  );
}
